import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/utils";
import { TOGGLE_LINK_COMMAND } from "@lexical/link";
import {
  $getSelection,
  $isElementNode,
  $isRangeSelection,
  FORMAT_ELEMENT_COMMAND,
  FORMAT_TEXT_COMMAND,
  type LexicalEditor,
} from "lexical";
import { ALargeSmall, ChevronDown, MessageSquare } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  CanvasFloatingToolbarFrame,
  CanvasToolbarButton,
  CanvasToolbarDivider,
  canvasToolbarButtonVariants,
} from "../components/toolbar";
import {
  CanvasToolbarAlignMenu,
  CanvasToolbarFormatMenu,
} from "../components/toolbar/CanvasToolbarFormatMenu";
import { applyCanvasListType } from "../components/toolbar/canvasListCommands";
import { LABEL_COLORS } from "./shared/CanvasColorPalettes";
import { CanvasEmojiPicker } from "./shared/CanvasEmojiPicker";
import { NodeToolbarContextMenuButton } from "./shared/NodeToolbarContextMenuButton";

interface TextLabelFloatingToolbarProps {
  visible: boolean;
  /** Omit with `onTextColorChange` to hide the text color picker (e.g. shape labels). */
  textColor?: string;
  fontFamily?: string;
  fontSize?: string;
  isBold?: boolean;
  isItalic?: boolean;
  isUnderline?: boolean;
  onTextColorChange?: (color: string) => void;
  onFontFamilyChange: (fontFamily: string) => void;
  onFontSizeChange: (fontSize: string) => void;
  onBoldChange: () => void;
  onItalicChange?: () => void;
  onUnderlineChange?: () => void;
  onChatClick?: () => void;
  editor: LexicalEditor | null;
  onPopoverOpenChange?: (isOpen: boolean) => void;
  /** Override the text color palette. Defaults to LABEL_COLORS. */
  textColors?: typeof LABEL_COLORS;
}

// Font families — same as TextNote
const FONT_FAMILIES = [
  { name: "Simple", value: "Inter, sans-serif" },
  { name: "Bookish", value: "Georgia, serif" },
  { name: "Technical", value: "'Courier New', monospace" },
  { name: "Scribbled", value: "'Comic Sans MS', cursive" },
  { name: "Modern", value: "'Inter', system-ui, sans-serif" },
];

// Font sizes — scaled for canvas label use
const FONT_SIZES = [
  { name: "Small", value: "16px" },
  { name: "Medium", value: "20px" },
  { name: "Large", value: "32px" },
  { name: "Extra large", value: "48px" },
  { name: "Huge", value: "64px" },
];

export function TextLabelFloatingToolbar({
  visible,
  textColor,
  fontFamily = "system-ui, -apple-system, sans-serif",
  fontSize = "16px",
  isBold = false,
  isItalic = false,
  isUnderline = false,
  onTextColorChange,
  onFontFamilyChange,
  onFontSizeChange,
  onBoldChange,
  onItalicChange,
  onUnderlineChange,
  onChatClick,
  editor,
  onPopoverOpenChange,
  textColors = LABEL_COLORS,
}: TextLabelFloatingToolbarProps) {
  const [isFontFamilyOpen, setIsFontFamilyOpen] = useState(false);
  const [isFontSizeOpen, setIsFontSizeOpen] = useState(false);
  const [customFontSize, setCustomFontSize] = useState("");
  const [isStrikethrough, setIsStrikethrough] = useState(false);
  const [textAlignment, setTextAlignment] = useState<
    "left" | "center" | "right"
  >("left");

  // Refs updated synchronously before notify so parent latch never sees stale OR.
  const isFontFamilyOpenRef = useRef(false);
  const isFontSizeOpenRef = useRef(false);
  const isFormatOpenRef = useRef(false);
  const isAlignOpenRef = useRef(false);
  const isEmojiOpenRef = useRef(false);

  const notifyOpen = useCallback(() => {
    onPopoverOpenChange?.(
      isFontFamilyOpenRef.current ||
        isFontSizeOpenRef.current ||
        isFormatOpenRef.current ||
        isAlignOpenRef.current ||
        isEmojiOpenRef.current,
    );
  }, [onPopoverOpenChange]);

  const handleFontFamilyOpen = useCallback(
    (open: boolean) => {
      isFontFamilyOpenRef.current = open;
      setIsFontFamilyOpen(open);
      notifyOpen();
    },
    [notifyOpen],
  );

  const handleFontSizeOpen = useCallback(
    (open: boolean) => {
      isFontSizeOpenRef.current = open;
      setIsFontSizeOpen(open);
      notifyOpen();
    },
    [notifyOpen],
  );

  const handleFormatOpen = useCallback(
    (open: boolean) => {
      isFormatOpenRef.current = open;
      notifyOpen();
    },
    [notifyOpen],
  );

  const handleAlignOpen = useCallback(
    (open: boolean) => {
      isAlignOpenRef.current = open;
      notifyOpen();
    },
    [notifyOpen],
  );

  const handleEmojiOpen = useCallback(
    (open: boolean) => {
      isEmojiOpenRef.current = open;
      notifyOpen();
    },
    [notifyOpen],
  );

  // Track bullet-list state from the editor selection
  useEffect(() => {
    if (!editor) return;

    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          const anchorNode = selection.anchor.getNode();
          const element =
            anchorNode.getKey() === "root"
              ? anchorNode
              : anchorNode.getTopLevelElementOrThrow();
          setIsStrikethrough(selection.hasFormat("strikethrough"));
          if ($isElementNode(element)) {
            const formatType = element.getFormatType();
            if (formatType === "center" || formatType === "right") {
              setTextAlignment(formatType);
            } else {
              setTextAlignment("left");
            }
          }
        }
      });
    });
  }, [editor]);

  // For preset sizes show the name; for custom sizes show the raw value (e.g. "24px").
  const currentFontSizePreset = FONT_SIZES.find((f) => f.value === fontSize);
  const fontSizeLabel = currentFontSizePreset?.name ?? fontSize;

  const handleCustomFontSizeSubmit = useCallback(() => {
    const sizeNum = parseInt(customFontSize);
    if (!isNaN(sizeNum) && sizeNum >= 8 && sizeNum <= 72) {
      onFontSizeChange(`${sizeNum}px`);
      handleFontSizeOpen(false);
      setCustomFontSize("");
    }
  }, [customFontSize, onFontSizeChange, handleFontSizeOpen]);

  const handleStrikethroughChange = useCallback(() => {
    editor?.dispatchCommand(FORMAT_TEXT_COMMAND, "strikethrough");
  }, [editor]);

  const handleAlignChange = useCallback(
    (align: "left" | "center" | "right") => {
      editor?.dispatchCommand(FORMAT_ELEMENT_COMMAND, align);
      setTextAlignment(align);
    },
    [editor],
  );

  const handleHyperlinkClick = useCallback(() => {
    if (!editor) return;
    const url = window.prompt("Enter URL");
    if (url === null) return;
    editor.dispatchCommand(TOGGLE_LINK_COMMAND, url.trim() || null);
  }, [editor]);

  if (!visible) return null;

  return (
    <TooltipProvider delayDuration={300}>
      <CanvasFloatingToolbarFrame
        onMouseDown={(e) => {
          if ((e.target as HTMLElement).closest("input")) return;
          e.preventDefault();
        }}
      >
        {/* Chat — accent purple, grouped first */}
        {onChatClick && (
          <CanvasToolbarButton
            label="Chat about this"
            accent
            onClick={(e) => {
              e.stopPropagation();
              onChatClick();
            }}
          >
            <MessageSquare className="size-4" />
          </CanvasToolbarButton>
        )}

        {onChatClick && <CanvasToolbarDivider />}

        {/* Font Family */}
        <Popover open={isFontFamilyOpen} onOpenChange={handleFontFamilyOpen}>
          <Tooltip>
            <TooltipTrigger asChild>
              <PopoverTrigger asChild>
                <button
                  className={cn(
                    canvasToolbarButtonVariants({ hasDropdown: true }),
                  )}
                  onClick={(e) => e.stopPropagation()}
                >
                  <ALargeSmall className="size-4" />
                  <ChevronDown className="size-2.5" strokeWidth={2.5} />
                </button>
              </PopoverTrigger>
            </TooltipTrigger>
            <TooltipContent side="top">Typeface</TooltipContent>
          </Tooltip>
          <PopoverContent
            className="w-40 p-1"
            align="center"
            side="top"
            sideOffset={8}
            onClick={(e) => e.stopPropagation()}
          >
            {FONT_FAMILIES.map((font) => (
              <button
                key={font.value}
                onClick={() => {
                  onFontFamilyChange(font.value);
                  handleFontFamilyOpen(false);
                }}
                className={cn(
                  "w-full px-3 py-2 text-left text-sm rounded-lg transition-colors",
                  "hover:bg-n-95/8 dark:hover:bg-n-95/10",
                  fontFamily === font.value &&
                    "bg-primary/10 text-primary font-medium",
                )}
                style={{ fontFamily: font.value }}
              >
                {font.name}
              </button>
            ))}
          </PopoverContent>
        </Popover>

        {/* Font Size Dropdown */}
        <Popover open={isFontSizeOpen} onOpenChange={handleFontSizeOpen}>
          <Tooltip>
            <TooltipTrigger asChild>
              <PopoverTrigger asChild>
                <button
                  className={cn(
                    canvasToolbarButtonVariants({ hasDropdown: true }),
                  )}
                  onClick={(e) => e.stopPropagation()}
                >
                  <span className="text-xs font-medium">{fontSizeLabel}</span>
                  <ChevronDown className="h-3 w-3" />
                </button>
              </PopoverTrigger>
            </TooltipTrigger>
            <TooltipContent side="top">Font size</TooltipContent>
          </Tooltip>
          <PopoverContent
            className="w-40 p-1"
            align="center"
            side="top"
            sideOffset={8}
            onClick={(e) => e.stopPropagation()}
          >
            {FONT_SIZES.map((size) => (
              <button
                key={size.value}
                onClick={() => {
                  onFontSizeChange(size.value);
                  handleFontSizeOpen(false);
                }}
                className={cn(
                  "w-full px-3 py-2 text-left text-sm rounded-lg transition-colors whitespace-nowrap",
                  "hover:bg-n-95/8 dark:hover:bg-n-95/10",
                  currentFontSizePreset?.value === size.value &&
                    "bg-primary/10 text-primary font-medium",
                )}
              >
                {size.name}
              </button>
            ))}
            {/* Custom size input */}
            <div className="pt-1 mt-1 border-t">
              <input
                type="number"
                placeholder="16"
                min="8"
                max="72"
                value={customFontSize}
                onChange={(e) => setCustomFontSize(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleCustomFontSizeSubmit();
                  }
                  e.stopPropagation();
                }}
                onClick={(e) => e.stopPropagation()}
                className="w-full px-3 py-2 text-sm rounded-lg bg-neutral-0 border outline-none focus:ring-2 focus:ring-canvas-selection"
              />
            </div>
          </PopoverContent>
        </Popover>

        {/* Separator */}
        <CanvasToolbarDivider />

        <CanvasToolbarFormatMenu
          isBold={isBold}
          isItalic={isItalic}
          isUnderline={isUnderline}
          isStrikethrough={isStrikethrough}
          textColor={textColor}
          textColors={textColors}
          onBoldChange={onBoldChange}
          onItalicChange={onItalicChange}
          onUnderlineChange={onUnderlineChange}
          onStrikethroughChange={handleStrikethroughChange}
          onTextColorChange={onTextColorChange}
          onListTypeSelect={(type) => applyCanvasListType(editor, type)}
          onHyperlinkClick={handleHyperlinkClick}
          onOpenChange={handleFormatOpen}
        />

        <CanvasToolbarAlignMenu
          alignment={textAlignment}
          onAlignChange={handleAlignChange}
          onOpenChange={handleAlignOpen}
        />

        {/* Emoji */}
        <CanvasEmojiPicker
          editor={editor}
          onPopoverOpenChange={handleEmojiOpen}
        />

        <CanvasToolbarDivider />

        <NodeToolbarContextMenuButton />
      </CanvasFloatingToolbarFrame>
    </TooltipProvider>
  );
}
